import { NON_CATALOG_INTEGRATION_SEGMENTS_PATTERN } from './integration-segments';
import { useLayoutEffect } from 'react';
import { atom } from 'nanostores';
import { useStore } from '@nanostores/react';
import { activeRouteStore, setActiveRoute } from '@customerio/hydra-nav-state';
import { AccountFeatureFlag, accountHasFeatureFlag } from '@/services/auth/feature-flags';
import { isBuilderPlan } from '@/services/auth/plan-slugs';
import { accountStore } from '@/services/auth/store';
import type { AugmentedAccount } from '@/services/auth/types';
import { AppBundle } from '@/utils/app-bundles';

type RouteOwner = 'hydra' | 'embedded';

export type RouteRepo = 'hydra' | 'ui' | 'embedded';

/**
 * The repository serving the current route: `hydra` (native React), `ui`
 * (embedded Ember Journeys), or `embedded` for any other embedded app.
 */
export function getRouteRepo(routeOwner: RouteOwner, routeBundle: AppBundle | null): RouteRepo {
  if (routeOwner === 'hydra') return 'hydra';
  if (routeBundle === AppBundle.Journeys) return 'ui';
  return 'embedded';
}

/** The slice of the account that route ownership depends on. */
export type RouteOwnershipAccount =
  | (Pick<AugmentedAccount, 'feature_flags'> & Partial<Pick<AugmentedAccount, 'is_masq' | 'plan_slug'>>)
  | null;

type RouteOwnershipPattern = {
  owner: RouteOwner;
  pattern: RegExp;
  /**
   * When set, the entry only applies while the account feature flag is on — a rollout
   * kill-switch: flag off, and the URL falls through to the default `embedded` owner. Before
   * the account loads, the flag reads its default (off ⇒ embedded), so any consumer that
   * evaluates ownership during render and caches the answer MUST re-evaluate once the
   * account arrives — use `useRouteOwner`/`useIsHydraRoute` for that.
   */
  flag?: AccountFeatureFlag;
  /**
   * The mirror of `flag`: the entry only applies while the account feature flag is **off**.
   * For a surface being hidden rather than migrated — Hydra claims the URL so it can render
   * its own `NotFoundPage`, and hands the URL back to the embedded app once the flag turns
   * on. Same pre-account caveat as `flag`: before the account loads the flag reads its
   * default, so a flag defaulting to off classifies as `hydra` until the real account
   * arrives — render-time consumers must use `useRouteOwner`/`useIsHydraRoute`.
   */
  unlessFlag?: AccountFeatureFlag;
  /** When true, masquerade falls through to the embedded owner even if the flag is off. */
  unlessMasq?: boolean;
  /** Builder accounts keep campaign tests in Journeys. */
  unlessBuilderPlan?: boolean;
};

/**
 * Canonical route ownership map for URLs that cross the Hydra shell /
 * embedded-app boundary.
 *
 * TanStack owns exact route matching, but embedded apps still ask Hydra whether
 * a browser URL should stay in the shell. Keep those ownership decisions here
 * so active-route sync, global navigation helpers, and Ember interop do not
 * each grow their own handwritten route model.
 *
 * Entries with a `flag` are gated on an account feature flag (via `hasFeatureFlag`,
 * this module's only runtime data dependency) — every consumer, including the
 * `window.__hydra__.isHydraRoute` bridge Ember calls, reads the answer fresh per
 * navigation, so a flag flip propagates without any consumer changes.
 */
/**
 * Every data-index URL below a workspace, in either spelling. TanStack's
 * concrete routes handle the known pages and the data-index splat handles the
 * rest, so no deeper path can fall through to an embedded app.
 */
const DATA_INDEX_SHAPES = String.raw`(?:\/[^#?]*)?`;

const DATA_INDEX_PATTERN = new RegExp(
  String.raw`^\/workspaces\/(last|(\d+))\/data-index${DATA_INDEX_SHAPES}\/?(?:[#?]|$)`,
  'i',
);

const JOURNEYS_DATA_INDEX_PATTERN = new RegExp(
  String.raw`^\/workspaces\/(last|(\d+))\/journeys\/data-index${DATA_INDEX_SHAPES}\/?(?:[#?]|$)`,
  'i',
);

const ROUTE_OWNERSHIP_PATTERNS: Array<RouteOwnershipPattern> = [
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/(?:journeys\/)?objects(?:\/[^/#?]+(?:\/[^/#?]+)?)?\/?(?:[#?]|$)/i,
    flag: AccountFeatureFlag.ObjectsMigration,
  },
  // The object detail's tabs: Attributes and its edit page (FPS-2778), Relationships
  // (FPS-2786) and Activity (FPS-2795). The entry above stops two segments after
  // `objects`, so these deeper URLs need their own claim. The tabs are an explicit
  // alternation and each alternative ends terminally, in both spellings, which keeps
  // any deeper path falling through to the embedded app.
  {
    owner: 'hydra',
    pattern:
      /^\/workspaces\/(last|(\d+))\/(?:journeys\/)?objects\/[^/#?]+\/[^/#?]+\/(?:attributes(?:\/edit)?|relationships|activity)\/?(?:[#?]|$)/i,
    flag: AccountFeatureFlag.ObjectsMigration,
  },
  // Auth-entry routes migrated to Hydra (FPS-2341): login + its sub-steps,
  // logout, academy-login, and the legacy /users/login redirect.
  { owner: 'hydra', pattern: /^\/login(?:[#/?]|$)/i },
  { owner: 'hydra', pattern: /^\/logout(?:[#/?]|$)/i },
  { owner: 'hydra', pattern: /^\/academy-login(?:[#/?]|$)/i },
  { owner: 'hydra', pattern: /^\/users\/login(?:[#/?]|$)/i },
  // The Journeys paywall, with no rollout flag and no embedded fallback. The embedded app still sends
  // expired accounts to this URL, so ownership must stay claimed here.
  { owner: 'hydra', pattern: /^\/trial-expired\/journeys(?:[#/?]|$)/i },
  // Signup funnel (FPS-2851): the whole /signup tree plus its legacy /users/register and
  // /users/signup aliases, no flag gate. The Ember signup was deleted at the FPS-2872
  // cutover. /signup/$ is a splat route, so the whole-subtree claim is exact: unknown
  // segments replace-redirect to the entry step.
  { owner: 'hydra', pattern: /^\/signup(?:[#/?]|$)/i },
  { owner: 'hydra', pattern: /^\/users\/(register|signup)(?:[#/?]|$)/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/settings\/ai-business-profile/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/settings\/audit-logs/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/settings\/api-keys/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/settings\/api_credentials/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/settings\/auto_archive/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/settings\/edit/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/settings\/confirm_delete/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/settings\/geofences/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/settings\/merge_options/i },
  // LLM configuration (FPS-2355): the whole route set — index, /new, and
  // /:id/edit — is Hydra-owned.
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/settings\/llm_configuration/i },
  // Object-type settings (FPS-2357) — fully migrated, so the entry is
  // unconditional. The Ember route set was deleted in ui#15911, so there is no
  // fallback left to defer to: the whole route set — index, `/new`, and
  // `/edit/:id` — is Hydra's from the first render, before the account loads.
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/settings\/objects(?:\/|$|[#?])/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/settings\/language/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/settings\/anonymous_events/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/settings\/suppressions/i },
  // Consent settings (MESS-1329): a single leaf page. Unconditional so the route's own
  // consent_records flag guard runs (flag off redirects to the settings landing) instead
  // of the URL falling through to the embedded app, which has no such page. Terminal
  // match so deeper paths keep falling through to the `settings/$` splat.
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/settings\/consent\/?(?:[#?]|$)/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/settings\/actions\/sms-providers/i },
  // SMS registration shell (FPS-2758): only the canonical index is migrated; registration and
  // management descendants remain embedded until their own slices land.
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/settings\/actions\/sms\/?(?:[#?]|$)/i },
  // Twilio settings (FPS-2840): a single leaf page.
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/settings\/actions\/twilio\/?(?:[#?]|$)/i },
  // URL-parameters settings (FPS-2858): index + edit, Hydra-owned unconditionally (direct
  // cutover, no rollout flag). Each alternative ends terminally (optional trailing slash,
  // then query, hash, or end of path) so the claim covers exactly the two URLs the route
  // tree serves; a deeper path has no route behind it and must keep falling through to the
  // `settings/$` splat.
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/settings\/actions\/url_params(?:\/edit)?\/?(?:[#?]|$)/i },
  // Time-zone-match / geolocation settings (FPS-2868): a single leaf page, Hydra-owned
  // unconditionally (direct cutover, no rollout flag). Terminal match (optional trailing
  // slash, then query, hash, or end of path) so deeper paths keep falling through to the
  // `settings/$` splat.
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/settings\/actions\/time_zone_match\/?(?:[#?]|$)/i },
  // Slack channel settings (FPS-2830): a single leaf page, Hydra-owned unconditionally
  // (direct cutover, no rollout flag). Terminal match (optional trailing slash, then query,
  // hash, or end of path) so deeper paths keep falling through to the `settings/$` splat.
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/settings\/actions\/slack\/?(?:[#?]|$)/i },
  // LINE channel settings (FPS-2826): a single leaf page, Hydra-owned unconditionally
  // (direct cutover, no rollout flag). Terminal match (optional trailing slash, then query,
  // hash, or end of path) so deeper paths keep falling through to the `settings/$` splat.
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/settings\/actions\/line\/?(?:[#?]|$)/i },
  // Live notification types (FPS-4581): a single leaf page, Hydra-owned unconditionally (direct
  // cutover, no rollout flag). Terminal match (optional trailing slash, then query, hash, or end of
  // path) so deeper paths keep falling through to the `settings/$` splat. The `journeys` twin is
  // claimed so Ember-originated navigation to the old URL hands off to Hydra, whose
  // `journeys/settings/$` redirect forwards it to the non-`journeys` shape.
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/settings\/live-notification-types\/?(?:[#?]|$)/i },
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/journeys\/settings\/live-notification-types\/?(?:[#?]|$)/i,
  },
  // Airship channel settings (FPS-2834): a single leaf page, Hydra-owned unconditionally
  // (direct cutover, no rollout flag). Terminal match (optional trailing slash, then query,
  // hash, or end of path) so deeper paths keep falling through to the `settings/$` splat.
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/settings\/actions\/urban_airship\/?(?:[#?]|$)/i },
  // Push channel settings (FPS-2351): index, `ios` and `android`, Hydra-owned unconditionally.
  // Terminal match so deeper paths keep falling through to the `settings/$` splat.
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/settings\/actions\/push(?:\/(?:ios|android|web))?\/?(?:[#?]|$)/i,
  },
  // Mobile apps settings (FPS-2838): index, `create` and `edit/<id>`, Hydra-owned unconditionally.
  // The id is any single segment rather than digits because the edit route's `$appId` is
  // unconstrained and ranks above the `settings/$` splat, so Hydra renders a non-numeric id and
  // sends it back to the list. Terminal match, so anything deeper, and an `edit` with no id, falls
  // through to the splat.
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/settings\/actions\/apps(?:\/(?:create|edit\/[^/#?]+))?\/?(?:[#?]|$)/i,
  },
  // In-app and notification inbox settings (FPS-2846): a single leaf page, Hydra-owned
  // unconditionally. Terminal match (optional trailing slash, then query, hash, or end of path) so
  // deeper paths keep falling through to the `settings/$` splat.
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/settings\/actions\/in_app\/?(?:[#?]|$)/i,
  },
  // WhatsApp channel settings (FPS-2847): a single leaf page, Hydra-owned unconditionally.
  // Terminal match (optional trailing slash, then query, hash, or end of path) so deeper paths
  // keep falling through to the `settings/$` splat.
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/settings\/actions\/whatsapp\/?(?:[#?]|$)/i,
  },
  // Knowledge library (FPS-4580): a single leaf page, Hydra-owned unconditionally now that the
  // `knowledge-library-migration` rollout flag is removed. Terminal matches (optional trailing slash,
  // then query, hash, or end of path) so deeper paths keep falling through to the `settings/$`
  // splat. The `journeys` twin is claimed so Ember-originated navigation to the old URL hands off to
  // Hydra, whose `journeys/settings/$` redirect forwards it to the non-`journeys` shape.
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/settings\/knowledge_library\/?(?:[#?]|$)/i },
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/journeys\/settings\/knowledge_library\/?(?:[#?]|$)/i,
  },
  // Message frequency limits (FPS-2871): the page and the `messaging_limit` URL it was
  // reachable at before the rename, both Hydra-owned unconditionally (direct cutover, no
  // rollout flag). Terminal matches (optional trailing slash, then query, hash, or end of
  // path) so deeper paths keep falling through to the `settings/$` splat.
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/settings\/actions\/message_frequency_limits\/?(?:[#?]|$)/i,
  },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/settings\/actions\/messaging_limit\/?(?:[#?]|$)/i },
  // Email settings (FPS-2349): the whole tabbed route set — sending domains (list + detail),
  // dynamic addresses, custom SMTP, suppression list, defaults, and the legacy redirects — is
  // Hydra-owned unconditionally now that the `hydra-email-settings` rollout flag is removed.
  //
  // The tabs are enumerated rather than claiming the whole `/email` subtree because
  // `/email/account_verification` is served by the embedded app and is not part of this migration —
  // it must keep falling through to the `settings/$` splat.
  //
  // Every alternative ends terminally (optional trailing slash, then query, hash, or end of path),
  // so the claim covers exactly the URLs the route tree serves: the email root, each tab, and one
  // sending domain by id. A deeper path such as `/smtp/anything` or `/sending_domains/42/anything`
  // has no route behind it, so it must fall through rather than be claimed and 404 in Hydra.
  {
    owner: 'hydra',
    pattern:
      /^\/workspaces\/(last|(\d+))\/settings\/actions\/email(?:\/(?:sending_domains(?:\/[^/#?]+)?|dynamic_addresses|from_addresses|deliverability|smtp|defaults|suppression_list))?\/?(?:[#?]|$)/i,
  },
  // These legacy settings routes only redirect. Claim their whole subtree so the embedded-history
  // fast path loads the redirect route and mounts the destination (FPS-4392).
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/journeys\/settings(?:\/|[#?]|$)/i,
  },
  // Subscription center (FPS-2356): the whole route set is Hydra-owned unconditionally now that the
  // `hydra-subscription-center` rollout flag is removed.
  //
  // Claims exactly the six URLs the route tree serves — the Subscriptions tab (index), the Settings
  // tab, the `topics` redirect, the bulk preference table, the branding editor and the localization
  // editor — each with an optional trailing slash, query, or hash. Deeper paths have no route behind
  // them and keep falling through to the `settings/$` splat. The legacy `journeys/settings` subtree
  // claim above also covers the old URL shape so Ember-originated navigation hands off to Hydra.
  {
    owner: 'hydra',
    pattern:
      /^\/workspaces\/(last|(\d+))\/settings\/subscription_center(?:\/(?:settings|topics(?:\/set_preferences)?|branding\/edit|localization\/edit))?\/?(?:[#?]|$)/i,
  },
  // Webhook configuration (FPS-2354): the whole subtree — index list, `/new`,
  // and `/:id/edit`.
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/settings\/webhook_configuration(?:\/|$|[#?])/i,
  },
  // Account settings landing page (FPS-2287), unflagged. Matches ONLY the bare `/settings` path
  // (with an optional trailing slash, query, or hash). Unknown `/settings/*` subpaths must keep
  // falling through to the `settings/$` splat and render the embedded Ember app, so this is
  // deliberately not the whole subtree. Rollback = revert this entry (and the index route swap).
  { owner: 'hydra', pattern: /^\/settings\/?(?:[#?]|$)/i },
  { owner: 'hydra', pattern: /^\/settings\/ai(?:[#/?]|$)/i },
  { owner: 'hydra', pattern: /^\/settings\/slack-agent(?:[#/?]|$)/i },
  { owner: 'hydra', pattern: /^\/settings\/account(?:[#/?]|$)/i },
  // Partner used to live under /settings/account/partner (covered by the
  // /settings/account pattern above); now it's a top-level settings path.
  { owner: 'hydra', pattern: /^\/settings\/partner(?:[#/?]|$)/i },
  { owner: 'hydra', pattern: /^\/settings\/personal\/google-identities(?:[#/?]|$)/i },
  // Both /settings/personal entries below are unconditional. `personal_settings_in_hydra` hit 100%
  // progressive rollout on 2026-09-17 (FlightDeck `fully_rolled_out_at`), a week before the gate
  // came off, so no account changes app on merge. Its `account_ids: [77983]` is the dogfood allow
  // list, not a deny field, so it's inert at 100%. The Ember pods are deleted in ui#15705.
  //
  // Personal 2FA management (FPS-2292). Enumerates the five screens rather than claiming the
  // subtree: there is no splat route under `/settings/personal/security`, so a subtree claim
  // would answer "hydra" for an unknown nested URL the embedded Ember app still renders.
  {
    owner: 'hydra',
    pattern: /^\/settings\/personal\/security(?:\/(new|new_totp|backup_codes|new_backup_codes))?\/?(?:[#?]|$)/i,
  },
  // Personal settings index (FPS-2861). Matches ONLY the index path — the security screens are
  // matched by the entry above, and `/settings/personal/google-identities` has its own.
  {
    owner: 'hydra',
    pattern: /^\/settings\/personal\/?(?:[#?]|$)/i,
  },
  { owner: 'hydra', pattern: /^\/settings\/roles(?:[#/?]|$)/i },
  // Team members: the index (list), `/add` (invite), `/members/:id/edit`
  // (FPS-2537), `/invite-guest` (FPS-2557), and `/join-requests/:id` (the approve
  // page) are Hydra-owned. `/set_password` is still Ember, so this matches only
  // those five shapes and leaves it to hand off to Ember. Widen to the full
  // subtree once set_password migrates.
  {
    owner: 'hydra',
    pattern: /^\/settings\/team(?:\/add|\/invite-guest|\/members\/[^/]+\/edit|\/join-requests\/[^/]+)?\/?(?:[#?]|$)/i,
  },
  // The workspaces list and create (/new) are both Hydra-owned now.
  { owner: 'hydra', pattern: /^\/settings\/workspaces(?:[#/?]|$)/i },
  { owner: 'hydra', pattern: /^\/settings\/privacy/i },
  { owner: 'hydra', pattern: /^\/settings\/api-keys/i },
  { owner: 'hydra', pattern: /^\/settings\/api_credentials/i },
  { owner: 'hydra', pattern: /^\/settings\/audit-logs/i },
  { owner: 'hydra', pattern: /^\/settings\/experimental-features/i },
  // Security settings (FPS-2223), fully rolled out at the FPS-2767 cutover: no flag gate,
  // and the Ember pods are deleted. Matches ONLY the two real routes — the index and the
  // nested `/sso` page (each with an optional trailing slash, query, or hash). Deliberately
  // not the whole subtree: there is no layout or splat route under `/settings/security`, so
  // an unknown nested URL resolves to the `settings/$` splat and renders the embedded app.
  // Claiming it here would make ownership say `hydra` while Ember is on screen — and after
  // the teardown Ember cannot route it either. Widen only alongside a real Hydra route.
  { owner: 'hydra', pattern: /^\/settings\/security(?:\/sso)?\/?(?:[#?]|$)/i },
  // Plans & Billing: the whole `/settings/billing` subtree — shell plus every tab — is Hydra-owned
  // unconditionally. The subtree is owned as one unit, never split per-tab, so one pattern covers
  // it.
  { owner: 'hydra', pattern: /^\/settings\/billing(?:\/|$|[#?])/i },
  // `/settings/receipts` redirects into billing history. Match only the bare path —
  // `/settings/receipts/:id` is a server-rendered Stripe receipt served outside this app, so it
  // must not be captured here.
  { owner: 'hydra', pattern: /^\/settings\/receipts(?:$|[#?])/i },
  // `/settings/subscription` (FPS-2286) redirects into the Your Plan tab, owned here on the same
  // footing as the billing subtree above. Matches the bare path plus an optional trailing slash,
  // query, or hash — like `/settings` above, and unlike `/settings/receipts`, which must exclude
  // the trailing slash to protect its `:id` sub-path. There is no route beneath this one, so a
  // nested URL resolves to the `settings/$` splat and renders the embedded app.
  { owner: 'hydra', pattern: /^\/settings\/subscription\/?(?:[#?]|$)/i },
  // The top-level, chrome-less promo checkout page. Outside the billing tab area, but owned here on
  // the same footing as the billing subtree above.
  { owner: 'hydra', pattern: /^\/checkout(?:\/|$|[#?])/i },
  // Essentials onboarding (SELF-138): the chrome-less /onboarding pages. Net-new
  // Hydra routes with no embedded equivalent, so the entry is UNGATED on purpose —
  // gating ownership on `essentials_onboarding` would hand flag-off traffic to
  // the embedded app, which can only boot into a 404. Flag-off users get
  // Hydra's NotFoundPage from the route layout's FeatureFlagGuard instead.
  // Verified against customerio/ui's router.js: no URL space under /onboarding
  // exists there — the only "onboarding" route names are signup children
  // (/signup/onboarding-usage), which this pattern does not touch. CDP and
  // Design Studio bundles never receive
  // top-level URLs (getAppBundleFromUrl routes those by their own prefixes).
  { owner: 'hydra', pattern: /^\/onboarding(?:\/|$|[#?])/i },
  { owner: 'hydra', pattern: /^\/har-sanitizer/i },
  // Guest-invitation accept page: Hydra-owned. Reached from an email link; the Ember route is
  // retained only as a revert target until the teardown.
  { owner: 'hydra', pattern: /^\/accept-guest-invitation(?:[#/?]|$)/i },
  { owner: 'hydra', pattern: /^\/no_accessible_workspaces/i },
  { owner: 'hydra', pattern: /^\/post_cancel/i },
  // The two legacy cancel URLs stay with the embedded app. Its routes redirect to this URL.
  { owner: 'hydra', pattern: /^\/account_canceled/i },
  { owner: 'hydra', pattern: /^\/confirm_account_notice/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/agent/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/tasks/i },
  // Boards (dashboard editor PoC): net-new Hydra-only routes with no embedded equivalent,
  // so the entry is UNGATED on purpose (like /onboarding above) — flag-off traffic must get
  // Hydra's NotFoundPage from the route's FeatureFlagGuard, not the embedded app.
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/boards/i },
  // Both taxonomy URL shapes: `analysis` pre-rename, `reports` once the flag is on. The
  // non-canonical one redirects to the other, but Hydra owns and serves both.
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/(analysis|reports)\/deliverability/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/(analysis|reports)\/email-clients/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/(analysis|reports)\/gmail-postmaster/i },
  // This legacy Journeys URL only redirects to the Hydra deliverability page (FPS-4625).
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/journeys\/analysis\/deliverability\/?(?:[#?]|$)/i },
  // The report itself (FPS-2303). Four URLs, because the taxonomy rename
  // doubles the segment and the page it replaced lived under `/journeys`: the
  // two `journeys` shapes are claimed so an in-app link to the old URL reaches
  // Hydra, whose redirect routes forward it to the canonical one. Each
  // alternative ends terminally, so the two pages above keep their own claims.
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/(?:journeys\/)?(analysis|reports)\/?(?:[#?]|$)/i,
  },
  // The Broadcasts list (FPS-2322), both tabs, behind its rollout flag. The page lives at
  // `/broadcasts`, without the `journeys` segment; the `journeys` shapes are the URLs the
  // embedded app and every existing link use, and are claimed so an in-app link reaches the
  // forward in `pages/workspaces/broadcasts/routing.tsx`. `newsletters` is the pre-rename
  // spelling of the one-time sends tab and only exists under `journeys`. A send's or a
  // broadcast's own pages under `/journeys/broadcasts` are not migrated and stay embedded, which is
  // why each alternative ends terminally.
  {
    owner: 'hydra',
    pattern:
      /^\/workspaces\/(last|(\d+))\/(?:journeys\/)?broadcasts(?:\/(?:one_time_sends|api-triggered))?\/?(?:[#?]|$)/i,
    flag: AccountFeatureFlag.BroadcastsMigration,
  },
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/journeys\/broadcasts\/newsletters\/?(?:[#?]|$)/i,
    flag: AccountFeatureFlag.BroadcastsMigration,
  },
  {
    owner: 'hydra',
    pattern:
      /^\/workspaces\/(?:last|\d+)\/(?:journeys\/)?broadcasts\/newsletter\/\d+\/overview(?:\/(?:reports|test|content|recipients|form-submissions|sent(?:\/[^/?#]+)?))?\/?(?:[#?]|$)/i,
    flag: AccountFeatureFlag.OneTimeSendOverviewMigration,
  },
  // The one-time send setup wizard (FPS-2325): every step, at `/broadcasts/newsletter/:id/setup`
  // and its `journeys` twin, under one rollout flag. The wizard migrates whole, so no step is
  // ever served by one app while its neighbours are served by the other.
  {
    owner: 'hydra',
    pattern:
      /^\/workspaces\/(?:last|\d+)\/(?:journeys\/)?broadcasts\/newsletter\/\d+\/setup(?:\/(?:recipients|tracking|content|schedule|review))?\/?(?:[#?]|$)/i,
    flag: AccountFeatureFlag.OneTimeSendSetupMigration,
  },
  // The list's other doors under `journeys` (FPS-2643): the `welcome` stub and the id-stripped
  // `newsletter` and `broadcast` URLs, which Ember sends on to the list. Each ends terminally, so a
  // send's or a broadcast's own pages keep their own owner.
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/journeys\/broadcasts\/(?:welcome|newsletter|broadcast)\/?(?:[#?]|$)/i,
    flag: AccountFeatureFlag.BroadcastsMigration,
  },
  // The create form (FPS-2643), under the same flag, at `/broadcasts/new` and its `journeys` twin.
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/(?:journeys\/)?broadcasts\/new\/?(?:[#?]|$)/i,
    flag: AccountFeatureFlag.BroadcastsMigration,
  },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/health/i },
  // The legacy tasks URL only redirects to the Hydra health page (FPS-4625).
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/journeys\/tasks\/?(?:[#?]|$)/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/home/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/pipelines/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/geosets/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/journeys\/geofences/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/message-library/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/journeys\/message-library/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/sign-up-forms\/?(?:[#?]|$)/i },
  // One form's stepper page. Its Build step links out to the editor at
  // `/design-studio/editor/sign-up-form/:id`, which stays embedded-owned, so
  // every hop between the two crosses owners and gets a full route load rather
  // than the embedded-history fast path (see `installEmbeddedAppHistorySync`).
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/sign-up-forms\/[^/?#]+\/?(?:[#?]|$)/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/snippets/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/experiments/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/journeys\/snippets/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/assets/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/journeys\/assets/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/(journeys\/)?exports/i },
  // Collections (SPE-3918), fully rolled out at the SPE-3923 cutover: `collections_in_hydra` hit
  // 100% on 2026-08-13 (FlightDeck `fully_rolled_out_at`), before the gate came off, so no account
  // changes app on merge; the Ember collections pods are deleted in ui#16131. The pages live at
  // `/collections`, without the `journeys` segment; the old `journeys` URLs are redirect routes
  // to them. Both patterns claim only the ported subpaths (`data`/`importing`/`usage`); anything
  // deeper under `journeys` falls through to `embedded`.
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/collections(?:\/[^/?#]+(?:\/(?:data|importing|usage))?)?\/?(?:[#?]|$)/i,
  },
  {
    owner: 'hydra',
    pattern:
      /^\/workspaces\/(last|(\d+))\/journeys\/collections(?:\/[^/?#]+(?:\/(?:data|importing|usage))?)?\/?(?:[#?]|$)/i,
  },
  // Profile activity (FPS-2304). The page lives at `/profile_activity`, without the `journeys`
  // segment. Both old `journeys` spellings (`profile_activity`, legacy `activity_logs`) are
  // redirect routes to it, and stay claimed so Ember-originating navigation reaches them.
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/profile_activity(?:\/|$|[#?])/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/journeys\/activity_logs/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/journeys\/profile_activity/i },
  // Goals (FLO-4772) — the one INVERTED entry in this map. Claiming a URL in order to refuse it
  // is not new (`/onboarding` and `/boards` above both do it, so flag-off traffic gets Hydra's
  // NotFoundPage rather than the embedded app); what is new here is that those two are net-new
  // Hydra URLs with no embedded equivalent, so they can claim UNCONDITIONALLY. Goals has a
  // working embedded page behind it, which flag-on must keep serving — an unconditional claim
  // would leave ownership saying `hydra` while Ember is on screen. Hence `unlessFlag` rather
  // than a bare entry, and rather than `flag`, whose sense is the opposite of what is needed.
  //
  // With the flag off the `/journeys/goals` routes render Hydra's NotFoundPage (see
  // `routes/-goals-route.tsx`), and ownership has to agree for a reason that is load-bearing
  // rather than cosmetic: `installEmbeddedAppHistorySync` short-circuits `router.load` whenever
  // ownership says both the current and the next URL belong to the same embedded bundle, so a
  // client-side navigation from any other Journeys page into a goals URL would skip the TanStack
  // route load and the route's FeatureFlagGuard would never run. Masquerade is exempt from that
  // refusal, so it falls through to the embedded owner even while the flag is off. Claims the
  // whole subtree, which is exactly what the two routes (`goals/` and `goals/$`) serve.
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/journeys\/goals(?:\/|$|[#?])/i,
    unlessFlag: AccountFeatureFlag.ShowGoals,
    unlessMasq: true,
  },
  // Outbox tabs + landing paths (FPS-2305 migration, FPS-2595 cutover), across both taxonomy
  // segments (`outbox` current, `message_activity` future). Fully rolled out at the FPS-3422 flag
  // removal: `outbox-migration` hit 100% on 2026-08-12 (FlightDeck `fully_rolled_out_at`, progressive
  // rollout and new signups both 100%, no per-account overrides), before the gate came off, so no
  // account changes app on merge; the Ember outbox pods are frozen in ui#16072 and deleted in
  // ui#16225. Owned: the Deliveries/Sent tab + its `/:deliveryId` detail, the Drafts tab **list**,
  // and the index + `/welcome` landing paths (`OutboxRouteView` redirects those to the default
  // tab). Both tab-name spellings (`deliveries`/`sent`) are claimed in each segment so a
  // cross-rename URL normalizes in React instead of booting Ember. The draft detail/edit
  // (`/drafts/:id`…) and any deeper path aren't migrated, so they fall through to `embedded`.
  //
  // The pages live at `/message_activity`, without the `journeys` segment; the two `journeys`
  // patterns below are the old URLs, which forward there. The Hydra route claims its whole subtree:
  // it sends the draft detail/edit and deeper paths back to the `journeys` URL, which Ember serves.
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/message_activity(?:\/|$|[#?])/i },
  {
    owner: 'hydra',
    pattern:
      /^\/workspaces\/(last|(\d+))\/journeys\/outbox(?:\/(?:(?:deliveries|sent)(?:\/[^/]+)?|drafts|welcome))?\/?(?:[#?]|$)/i,
  },
  {
    owner: 'hydra',
    pattern:
      /^\/workspaces\/(last|(\d+))\/journeys\/message_activity(?:\/(?:(?:deliveries|sent)(?:\/[^/]+)?|drafts|welcome))?\/?(?:[#?]|$)/i,
  },
  // Redirect-only Journeys URLs, forwarded natively (FPS-2346) — see
  // `routes/-journeys-redirects.tsx`. Unflagged: each is a pure forward with no page of its
  // own, so there is nothing for a rollout flag to dark-launch. The Ember pods stay frozen
  // in place as the revert target until the teardown.
  //
  // Each pattern matches only the URL shapes Ember registered, so anything deeper keeps
  // falling through to the embedded app exactly as it does today.
  //
  // The pre-Outbox top-level `/drafts` and `/deliveries`, which forward to the Outbox tabs.
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/journeys\/drafts\/?(?:[#?]|$)/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/journeys\/deliveries\/?(?:[#?]|$)/i },
  // The pre-v2 `triggered` name for campaigns: the list and the create page, nothing else.
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/journeys\/triggered(?:\/new)?\/?(?:[#?]|$)/i },
  // `v2/*` URLs from before the v2 app dropped its path prefix.
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/journeys\/v2(?:\/|$|[#?])/i },
  // `/setup-guide` and everything under it, which forward to the dashboard. This is the
  // bookmark/marketing-link stub only — the real setup guide lives in workspace settings and
  // is not touched by this entry.
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/journeys\/setup-guide(?:\/|$|[#?])/i },
  // The entire layouts feature is Hydra-owned now (FPS-2585): list
  // (FPS-2299), starters (FPS-2556), detail (FPS-2571), and create/edit
  // (FPS-2555). The pages live at `/layouts`, without the `journeys` segment. The old
  // `/journeys/layouts` subtree is redirect routes to them and stays claimed.
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/layouts(?:\/|$|[#?])/i },
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/journeys\/layouts(?:\/|$|[#?])/i },
  // Standalone template dispatcher (FPS-2300). The trailing guard keeps the
  // pattern from claiming Ember's `/journeys/composer/.../templates/...` URLs.
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/(journeys\/)?template(?:[#/?]|$)/i },
  // Integrations directory (FPS-2314), unconditional since the `hydra-integrations-directory`
  // rollout flag reached 100% and was removed — the Ember directory pods are gone, so Hydra is
  // the only thing serving these URLs. TWO entries, because the migration drops the `journeys`
  // segment from the URL:
  //
  //   1. `/integrations/...` — where the migrated page actually lives.
  //   2. `/journeys/integrations/...` — the old Ember URL, kept claimed purely so
  //      `JourneysIntegrationsHandoff` can forward it to (1). Nothing in Hydra links here any
  //      more; it exists for bookmarks and any stale link still pointing at the Ember spelling.
  //
  // Both match ONLY the six real routes — the `/integrations` and `/integrations/all` indexes
  // (each redirecting to the Connections tab), the `/all/overview` and `/all/directory` tabs,
  // and the URL-only `/all/developers` + `/all/webhooks` sub-tabs (FPS-2655; reachable by
  // direct URL, absent from the tab nav) — each with an optional trailing slash, query, or
  // hash. Deliberately narrow: it claims the directory only. The nested connector routes
  // (`/sql-sync/*`, unported audience routes, …) have no Hydra route and must keep falling through to the
  // embedded app; the ones that do have a route (`/integrations/:type`, `warehouse`,
  // `reporting-webhooks`) are claimed by their own entries below, not by this one. Widen only
  // alongside a real Hydra route.
  {
    owner: 'hydra',
    pattern:
      /^\/workspaces\/(last|(\d+))\/integrations(?:\/all(?:\/(?:overview|directory|developers|webhooks))?)?\/?(?:[#?]|$)/i,
  },
  {
    owner: 'hydra',
    pattern:
      /^\/workspaces\/(last|(\d+))\/journeys\/integrations(?:\/all(?:\/(?:overview|directory|developers|webhooks))?)?\/?(?:[#?]|$)/i,
  },
  // Reporting webhooks (FPS-2648 + FPS-2659) — fully migrated, so both entries are unconditional.
  // The Ember route set was deleted in ui#16224. TWO entries, because the migration drops the
  // `journeys` segment from the URL:
  //
  //   1. `/integrations/reporting-webhooks/...` — where the migrated pages live.
  //   2. `/journeys/integrations/reporting-webhooks/...` — the old Ember URLs. Hydra claims them
  //      only so `JourneysReportingWebhooksHandoff` can forward to (1), which keeps existing links
  //      and bookmarks working now that Ember no longer serves them at all.
  //
  // Both match the WHOLE subtree — the list, `/new`, and the bare-id edit page.
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/integrations\/reporting-webhooks(?:\/|$|[#?])/i,
  },
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/journeys\/integrations\/reporting-webhooks(?:\/|$|[#?])/i,
  },
  // Native data-out connectors — `/integrations/data_out/:type` (mixpanel, amplitude), fully rolled
  // out (FPS-2800), so both entries claim unconditionally. The migrated URL drops `journeys`; the
  // `journeys` twin stays claimed so `JourneysDataOutHandoff` can forward to it, which keeps every
  // Ember-side directory link working until the teardown converts them.
  //
  // A connector type is required: `/data_out` with no `:type` is not a page in either app, so the
  // pattern demands a non-empty segment and leaves the bare path with the embedded app.
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/integrations\/data_out\/[^/]+\/?(?:[#?]|$)/i,
  },
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/journeys\/integrations\/data_out\/[^/]+\/?(?:[#?]|$)/i,
  },
  // Warehouse sync — the whole `/integrations/warehouse` tree (FPS-2317), migrated and rolled
  // out to 100% on 2026-08-31, so both entries are now unconditional. The flag carried no
  // per-account overrides that could hold it off: its config was an `account_ids` *allow* list
  // (the dogfood account) plus `progressive_rollout: 100` and `new_signups_rollout: 100%`, with
  // no deny-list field, so dropping the gate changed no account's answer. Same two-entry shape as the
  // two above: the migrated URL drops `journeys`, and the `journeys` twin is claimed only so
  // `JourneysWarehouseHandoff` can forward to it, which keeps every Ember-side link working
  // untouched.
  //
  // Every route *below* `/integrations/warehouse`: the three connector landing pages (FPS-2698),
  // the connected-config view keyed by storage provider (FPS-2717), and the create wizard at
  // `/:type/sync` (FPS-2730).
  //
  // The bare `/warehouse` path is deliberately excluded — it has no Hydra route, so claiming a URL
  // the shell cannot render would leave ownership saying `hydra` while the embedded app is on
  // screen, which wedges active-route sync until a reload. That reasoning is unchanged; the two
  // facts the original comment cited for it are not:
  //
  // - It said "Ember serves it". Ember's warehouse route set was torn down in the FPS-2755
  //   teardown (customerio/ui#16206), so nothing answers this path directly any more. Verified on
  //   production after that merge: visiting it lands on `/integrations/all/overview` rendering the
  //   Integrations page, with no visible error — the embedded app falls through to its integrations
  //   index, which Hydra owns. Graceful, but by redirect rather than by anything serving it.
  // - It said "the command palette links there". It no longer does — hydra#6069 replaced that
  //   entry with the three provider landing pages, because the bare path rendered nothing.
  //
  // So the exclusion stays, but do not widen it on the assumption that something is behind it.
  //
  // Both patterns terminate for the same reason: only `<type>` and `<type>/sync` are real routes,
  // so anything deeper (`/warehouse/s3/sync/extra`) stays with the embedded app.

  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/integrations\/warehouse\/[^/]+(?:\/sync)?\/?(?:[#?]|$)/i,
  },
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/journeys\/integrations\/warehouse\/[^/]+(?:\/sync)?\/?(?:[#?]|$)/i,
  },
  // SQL sync — the `/integrations/sql-sync` tree (FPS-2307), unconditionally Hydra-owned since
  // `sql-sync-migration` was removed (FPS-3858). Same two-entry shape as warehouse above: the
  // migrated URL drops `journeys`, and the `journeys` twin is claimed only so
  // `JourneysSqlSyncListHandoff` can forward to it.
  //
  // The gating comes off *before* the Ember routes are deleted (FPS-3857), not after. While both
  // the flag and the Ember pods existed, a flag-off account fell back to Ember. Removing the pods
  // first would leave a flag-off flip pointing at nothing, so the flag has to stop deciding
  // ownership while Ember can still answer.
  //
  // The per-database list, `/sql-sync/<type>` (FPS-2781), the create wizard, `/<type>/new`
  // (FPS-2789), the import detail, `/<type>/<id>` (FPS-2814), and its tabs, `/<id>/query`,
  // `/<id>/relationships-query` and `/<id>/settings` (FPS-2819), for the six engines; and the bare
  // `/sql-sync/<number>`, Ember's singular `sql-import` route, which loads the import and redirects
  // to its typed URL (FPS-2825). The bare `/sql-sync` has no page in either app.
  {
    owner: 'hydra',
    pattern:
      /^\/workspaces\/(last|(\d+))\/integrations\/sql-sync\/(?:(?:mysql|postgres|snowflake|bigquery|redshift|sqlserver)(?:\/new|\/\d+(?:\/(?:query|relationships-query|settings))?)?|\d+)\/?(?:[#?]|$)/i,
  },
  {
    owner: 'hydra',
    pattern:
      /^\/workspaces\/(last|(\d+))\/journeys\/integrations\/sql-sync\/(?:(?:mysql|postgres|snowflake|bigquery|redshift|sqlserver)(?:\/new|\/\d+(?:\/(?:query|relationships-query|settings))?)?|\d+)\/?(?:[#?]|$)/i,
  },
  // The Customer.io API connector page (FPS-2752), unconditionally Hydra-owned since the
  // rollout flag was removed (FPS-3724). Two entries because the migrated URL drops the `journeys`
  // segment: `/integrations/customerio` is where the page lives, and the `journeys` twin stays
  // claimed so `JourneysCustomerioHandoff` can forward the Ember-side links to it.
  //
  // Both patterns terminate at `customerio`: the page has no children, so anything deeper stays
  // with the embedded app. The sibling connector pages that migrated with it (`mparticle_out`,
  // the generic `:type` page) have their own entries below.
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/integrations\/customerio\/?(?:[#?]|$)/i,
  },
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/journeys\/integrations\/customerio\/?(?:[#?]|$)/i,
  },
  // The mParticle connector page (FPS-2756), which shipped and rolled out with its sibling above
  // under FPS-2316. Same two-entry shape and the same terminating patterns.
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/integrations\/mparticle_out\/?(?:[#?]|$)/i,
  },
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/journeys\/integrations\/mparticle_out\/?(?:[#?]|$)/i,
  },
  // The catch-all connector detail page — `/integrations/:type` (FPS-2761) — which rolled out with
  // the two connectors above. Same two-entry shape.
  //
  // Unlike its siblings this claims a *dynamic* segment, so it has to name what it must not
  // swallow. The list lives in `integration-segments.ts` because the `$type` route components
  // read it too — they behave as though unmigrated for those segments, so the ownership answer
  // and the page's behaviour cannot drift apart.
  {
    owner: 'hydra',
    pattern: new RegExp(
      `^/workspaces/(last|(\\d+))/integrations/(?!(?:${NON_CATALOG_INTEGRATION_SEGMENTS_PATTERN})(?:[/#?]|$))[^/]+/?(?:[#?]|$)`,
      'i',
    ),
  },
  {
    owner: 'hydra',
    pattern: new RegExp(
      `^/workspaces/(last|(\\d+))/journeys/integrations/(?!(?:${NON_CATALOG_INTEGRATION_SEGMENTS_PATTERN})(?:[/#?]|$))[^/]+/?(?:[#?]|$)`,
      'i',
    ),
  },
  // Ad-audience setup and settings (FPS-2796/FPS-2809), with the `journeys` segment removed from
  // the Hydra page URL. The old shapes stay claimed so they can forward when the migration is
  // enabled.
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/integrations\/audiences\/[^/#?]+\/setup\/?(?:[#?]|$)/i,
    flag: AccountFeatureFlag.AudiencesIntegrationsMigration,
  },
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/journeys\/integrations\/audiences\/[^/#?]+\/setup\/?(?:[#?]|$)/i,
    flag: AccountFeatureFlag.AudiencesIntegrationsMigration,
  },
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/integrations\/audiences\/[^/#?]+\/settings\/?(?:[#?]|$)/i,
    flag: AccountFeatureFlag.AudiencesIntegrationsMigration,
  },
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/journeys\/integrations\/audiences\/[^/#?]+\/settings\/?(?:[#?]|$)/i,
    flag: AccountFeatureFlag.AudiencesIntegrationsMigration,
  },
  // Forms list, integration card aliases, and detail routes (FPS-2848 / FPS-2894).
  {
    owner: 'hydra',
    pattern:
      /^\/workspaces\/(last|(\d+))\/integrations\/forms(?:\/(?:facebook|custom|typeform|jotform|in-app)(?:\/[^/#?]+)?)?\/?(?:[#?]|$)/i,
    flag: AccountFeatureFlag.FormsIntegrationsMigration,
  },
  {
    owner: 'hydra',
    pattern:
      /^\/workspaces\/(last|(\d+))\/journeys\/integrations\/forms(?:\/(?:facebook|custom|typeform|jotform|in-app)(?:\/[^/#?]+)?)?\/?(?:[#?]|$)/i,
    flag: AccountFeatureFlag.FormsIntegrationsMigration,
  },
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/journeys\/integrations\/facebook_lead_ads(?:\/[^/#?]+)?\/?(?:[#?]|$)/i,
    flag: AccountFeatureFlag.FormsIntegrationsMigration,
  },
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/integrations\/facebook_lead_ads(?:\/[^/#?]+)?\/?(?:[#?]|$)/i,
    flag: AccountFeatureFlag.FormsIntegrationsMigration,
  },
  // Forms settings (FPS-2897) adds the exact settings leaf while list descendants
  // remain embedded until their own migration lands.
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/integrations\/forms\/settings\/?(?:[#?]|$)/i,
    flag: AccountFeatureFlag.FormsIntegrationsMigration,
  },
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/journeys\/integrations\/forms\/settings\/?(?:[#?]|$)/i,
    flag: AccountFeatureFlag.FormsIntegrationsMigration,
  },
  // The People list (FPS-2311), unconditionally Hydra-owned since the rollout flag was removed
  // (FPS-4255). Same shape and reasoning as data-out above: the migrated URL drops `journeys`, and
  // the two `journeys` twins are claimed only so `JourneysPeopleHandoff` can forward to it.
  //
  // The migrated URL ships at both segments too — `people` is canonical and the route view
  // forwards the other — so both are claimed.
  //
  // All four patterns terminate at the index: everything deeper (`/journeys/people/:personId`
  // and its tabs) is still Ember's until its own migration lands.
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/people\/?(?:[#?]|$)/i,
  },
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/profiles\/?(?:[#?]|$)/i,
  },
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/journeys\/people\/?(?:[#?]|$)/i,
  },
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/journeys\/profiles\/?(?:[#?]|$)/i,
  },
  // The add-people catalog (`people.create` in Ember) — the same four spellings as
  // the list above, and claimed for the same reason: the migrated URL drops `journeys`, and both
  // `journeys` twins stay claimed so `JourneysPeopleCreateHandoff` can forward to it.
  //
  // Every pattern terminates at `create`, so sibling Ember routes that share the prefix
  // (`/journeys/people/:personId`) are untouched.
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/people\/create\/?(?:[#?]|$)/i,
  },
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/profiles\/create\/?(?:[#?]|$)/i,
  },
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/journeys\/people\/create\/?(?:[#?]|$)/i,
  },
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/journeys\/profiles\/create\/?(?:[#?]|$)/i,
  },
  // The add-a-person form (`people.new` in Ember) — the same four spellings as the
  // list and the catalog above, claimed for the same reason: the migrated URL drops `journeys` and
  // ships at both taxonomy segments, and both `journeys` twins stay claimed so
  // `JourneysPeopleNewHandoff` can forward to it.
  //
  // Every pattern terminates at `new`, so sibling Ember routes that share the prefix
  // (`/journeys/people/:personId`) are untouched.
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/people\/new\/?(?:[#?]|$)/i,
  },
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/profiles\/new\/?(?:[#?]|$)/i,
  },
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/journeys\/people\/new\/?(?:[#?]|$)/i,
  },
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/journeys\/profiles\/new\/?(?:[#?]|$)/i,
  },
  // The merge-two-people wizard (`people.merge` in Ember) — the same four spellings
  // as the list, the catalog and the form above, claimed for the same reason: the migrated URL drops
  // `journeys` and ships at both taxonomy segments, and both `journeys` twins stay claimed so
  // `JourneysPeopleMergeHandoff` can forward to it.
  //
  // Every pattern terminates at `merge`, so sibling Ember routes that share the prefix
  // (`/journeys/people/:personId`) are untouched.
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/people\/merge\/?(?:[#?]|$)/i,
  },
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/profiles\/merge\/?(?:[#?]|$)/i,
  },
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/journeys\/people\/merge\/?(?:[#?]|$)/i,
  },
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/journeys\/profiles\/merge\/?(?:[#?]|$)/i,
  },
  // The `welcome` landing paths (`people.welcome` in Ember, a bare `replaceWith('people')`) — the
  // same four spellings as the list, the catalog, the form and the wizard above,
  // claimed for the same reason: the migrated URL drops `journeys` and ships at both taxonomy
  // segments, and both `journeys` twins stay claimed so `JourneysPeopleWelcomeHandoff` can forward
  // to it.
  //
  // Every pattern terminates at `welcome`, so sibling Ember routes that share the prefix
  // (`/journeys/people/:personId`) are untouched.
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/people\/welcome\/?(?:[#?]|$)/i,
  },
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/profiles\/welcome\/?(?:[#?]|$)/i,
  },
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/journeys\/people\/welcome\/?(?:[#?]|$)/i,
  },
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/journeys\/profiles\/welcome\/?(?:[#?]|$)/i,
  },
  // The person profile's tabs this app serves: Overview, at the profile's own URL (FPS-2690), Attributes (FPS-4208) and its editor (FPS-2706), Relationships, Devices and Activity (FPS-2712), Drafts and Sent (FPS-2725), Segments with its Belongs to and Doesn't belong to views, the Journeys list with each journey's timeline (FPS-2783), and Live notifications with each notification's detail (FPS-4855). The
  // profile migrates tab by tab: Hydra renders the profile header and tab bar around these tabs,
  // and every sibling tab stays with the embedded app until it is added here.
  // Every tab's entry shares the `profile-page-migration` flag, so the flag gives an account
  // whichever tabs have shipped.
  //
  // Claimed at the canonical URL and at the `journeys` twin the embedded app's tab bar links to,
  // which forwards to the canonical one. Both patterns end at the profile or the tab's segment (or a Segments view, a journey id, or a live notification id). The person id is any
  // single segment: a profile is addressed by its internal id or by its external id.
  {
    owner: 'hydra',
    pattern:
      /^\/workspaces\/(last|(\d+))\/people\/[^/#?]+(?:\/(?:attributes(?:\/edit)?|relationships|segments(?:\/(?:belongs|others))?|devices|drafts|sent|activity|live-notifications(?:\/[^/#?]+)?|journeys(?:\/[^/#?]+)?))?\/?(?:[#?]|$)/i,
    flag: AccountFeatureFlag.ProfilePageMigration,
  },
  {
    owner: 'hydra',
    pattern:
      /^\/workspaces\/(last|(\d+))\/journeys\/people\/[^/#?]+(?:\/(?:attributes(?:\/edit)?|relationships|segments(?:\/(?:belongs|others))?|devices|drafts|sent|activity|live-notifications(?:\/[^/#?]+)?|journeys(?:\/[^/#?]+)?))?\/?(?:[#?]|$)/i,
    flag: AccountFeatureFlag.ProfilePageMigration,
  },
  // The whole data index (FPS-2310) is Hydra-owned. The canonical URL drops `journeys`, and the
  // twin is claimed so the handoff can forward old links to it.
  //
  // Concrete routes serve known lists and items; the sibling data-index splat
  // normalizes every other path to the resumed attributes list. Keeping both
  // patterns broad prevents a hard load from selecting an embedded bundle
  // before one of those Hydra routes can redirect it.
  {
    owner: 'hydra',
    pattern: DATA_INDEX_PATTERN,
  },
  {
    owner: 'hydra',
    pattern: JOURNEYS_DATA_INDEX_PATTERN,
  },

  // The classic dashboard (FPS-2301) is Hydra's for every workspace. The canonical URL drops the
  // `journeys` bundle segment; the old spelling remains claimed so its redirect can preserve
  // existing links and bookmarks. The rollout reached 100%, including new signups, on 2026-09-28.
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/dashboard\/?(?:[#?]|$)/i,
  },
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/journeys\/dashboard\/?(?:[#?]|$)/i,
  },

  // The imports section, unconditionally. Fully rolled out at the FPS-2306 flag removal:
  // `csv-imports-migration` hit 100% on 2026-09-14 (FlightDeck `fully_rolled_out_at`, progressive
  // rollout and new signups both 100%), a week before the gate came off, so no account changes app
  // on merge. Its config still carried `account_ids: [151876]` from the Leo Corp phase, which is
  // an allow list rather than a deny field — inert under a 100% progressive rollout, so dropping
  // the gate takes the feature from nobody.
  //
  // The migrated URL drops `journeys`, and the `journeys` twin stays claimed so
  // `JourneysImportsHandoff` can forward to it — Ember-side links and the embedded app's own nav
  // still point at the old spelling, and they are converted by the FPS-2702 teardown, not here.
  //
  // The list and the upload screen, in both spellings. `new` is matched exactly rather than as a
  // prefix, so an import id could never be mistaken for it.
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/imports(\/new)?\/?(?:[#?]|$)/i,
  },
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/journeys\/imports(\/new)?\/?(?:[#?]|$)/i,
  },
  // A finished import's own page. Matched as a single trailing segment so it cannot swallow the
  // wizard's URLs, which carry `/setup` after the id and are claimed below.
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/imports\/[^/]+\/?(?:[#?]|$)/i,
  },
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/journeys\/imports\/[^/]+\/?(?:[#?]|$)/i,
  },
  // The whole setup wizard, in both spellings: `setup` itself and each of its steps. The steps
  // stay named individually rather than matched as `setup/*`, which is now about not claiming a
  // URL no route serves rather than about an unmigrated step.
  {
    owner: 'hydra',
    pattern:
      /^\/workspaces\/(last|(\d+))\/imports\/[^/]+\/setup(\/(preprocessing|validating|importing|preview|fields|review))?\/?(?:[#?]|$)/i,
  },
  {
    owner: 'hydra',
    pattern:
      /^\/workspaces\/(last|(\d+))\/journeys\/imports\/[^/]+\/setup(\/(preprocessing|validating|importing|preview|fields|review))?\/?(?:[#?]|$)/i,
  },
  // The automation workflow builder (the canvas) — dark behind `workflow-builder-migration`
  // (FPS-2338). Four URL shapes flip together on this one flag:
  //
  //   - `/workspaces/:id/automations/:campaignId/workflow` — the canonical address. It drops
  //     the `journeys` segment (that segment names an embedded bundle, not a level of the
  //     product) and settles on `automations`, the canonical taxonomy spelling.
  //   - `/workspaces/:id/journeys/:segment/:campaignId/workflow` — the state-agnostic address
  //     that links and the flag-off hand-back use. Claimed so it forwards straight to the
  //     canonical one. Left to the embedded app, its own redirect to the setup or overview
  //     address would mount its builder first, and that builder would stay alive, hidden,
  //     behind this app's canvas, with its keyboard shortcuts still firing.
  //   - `/workspaces/:id/journeys/:segment/:campaignId/setup/workflow/actions` — the draft
  //     flow's address, claimed so the handoff route can forward it to the canonical one.
  //   - `/workspaces/:id/journeys/:segment/:campaignId/overview/workflow/actions` — the same
  //     address for a campaign past the draft state, claimed for the same reason.
  //
  // The overview URL is a tab of a tabbed page, and its six sibling tabs stay with the embedded
  // app. That is deliberate, and it is not a split of ownership: this page renders the tab bar
  // itself, as plain links carrying one canonical URL each. Which app answers a given tab is
  // resolved per click from this very map, so the six unclaimed tabs behave exactly as they did
  // and each migrates by adding an entry here — no portal, no per-tab branching in the nav.
  //
  // Both URL spellings are claimed for the `journeys` shapes: the `campaigns` segment
  // redirects to `automations`, so both remain live URLs.
  //
  // The API-triggered broadcast canvas (`/journeys/broadcasts/broadcast/:id/...`) is the same
  // component over the same model, but it is deliberately NOT claimed here: it comes in its own
  // migration. In-app broadcasts have no canvas at all.
  //
  // `:campaignId` is bounded to digits — campaign ids are numeric — and every pattern
  // terminates, so nothing deeper than the routes the tree actually serves answers `hydra`.
  //
  // Draft-mode accounts (`campaign-draft-mode`) edit through a draft session rather than the live
  // graph, and this app's canvas has no draft session yet. So all four shapes stay with the
  // embedded app for those accounts, whatever the rollout flag says.
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/automations\/\d+\/workflow\/?(?:[#?]|$)/i,
    flag: AccountFeatureFlag.WorkflowBuilderMigration,
    unlessFlag: AccountFeatureFlag.CampaignDraftMode,
  },
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/journeys\/(campaigns|automations)\/\d+\/workflow\/?(?:[#?]|$)/i,
    flag: AccountFeatureFlag.WorkflowBuilderMigration,
    unlessFlag: AccountFeatureFlag.CampaignDraftMode,
  },
  {
    owner: 'hydra',
    pattern:
      /^\/workspaces\/(last|(\d+))\/journeys\/(campaigns|automations)\/\d+\/setup\/workflow\/actions\/?(?:[#?]|$)/i,
    flag: AccountFeatureFlag.WorkflowBuilderMigration,
    unlessFlag: AccountFeatureFlag.CampaignDraftMode,
  },
  {
    owner: 'hydra',
    pattern:
      /^\/workspaces\/(last|(\d+))\/journeys\/(campaigns|automations)\/\d+\/overview\/workflow\/actions\/?(?:[#?]|$)/i,
    flag: AccountFeatureFlag.WorkflowBuilderMigration,
    unlessFlag: AccountFeatureFlag.CampaignDraftMode,
  },
  // The action composer — dark behind `composer-migration` (FPS-4673). Two URL shapes flip
  // together on this one area flag:
  //
  //   - `/workspaces/:id/composer/actions/:actionId` — the canonical address, without the
  //     `journeys` segment.
  //   - `/workspaces/:id/journeys/composer/actions/:actionId` — the address every existing link
  //     uses, claimed so its route can forward a migrated action to the canonical one.
  //
  // `composer-migration` is the area flag: it owns both URLs. A pattern cannot know the action's
  // type, and this app composes only the types whose own flag is on (`composer-<type>`, read by
  // the route's type gate). For any other action the route renders the embedded app at the
  // `journeys` address and claims that path back for it with `useEmbeddedFallbackClaim`, so the
  // embedded app routes there instead of deferring to this map.
  //
  // `:actionId` is any one segment, because an action id can carry a version suffix (`123-4`).
  // Both patterns terminate, and the transactional and newsletter composers stay with the
  // embedded app.
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/composer\/actions\/[^/]+\/?(?:[#?]|$)/i,
    flag: AccountFeatureFlag.ComposerMigration,
  },
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/journeys\/composer\/actions\/[^/]+\/?(?:[#?]|$)/i,
    flag: AccountFeatureFlag.ComposerMigration,
  },
  // Experiment-action migrates the campaign Tests overview and experiment details together.
  // Flag off leaves the existing Journeys tab and its legacy A/B-test details untouched.
  {
    owner: 'hydra',
    pattern:
      /^\/workspaces\/(last|(\d+))\/(campaigns|automations)\/\d+\/overview\/tests(?:\/experiments\/\d+)?\/?(?:[#?]|$)/i,
    flag: AccountFeatureFlag.ExperimentAction,
    unlessBuilderPlan: true,
  },
  {
    owner: 'hydra',
    pattern:
      /^\/workspaces\/(last|(\d+))\/journeys\/(campaigns|automations)\/\d+\/overview\/tests(?:\/experiments\/\d+)?\/?(?:[#?]|$)/i,
    flag: AccountFeatureFlag.ExperimentAction,
    unlessBuilderPlan: true,
  },
  {
    owner: 'hydra',
    pattern:
      /^\/workspaces\/(last|(\d+))\/broadcasts\/broadcast\/\d+\/overview\/tests(?:\/experiments\/\d+)?\/?(?:[#?]|$)/i,
    flag: AccountFeatureFlag.ExperimentAction,
    unlessBuilderPlan: true,
  },
  {
    owner: 'hydra',
    pattern:
      /^\/workspaces\/(last|(\d+))\/journeys\/broadcasts\/broadcast\/\d+\/overview\/tests(?:\/experiments\/\d+)?\/?(?:[#?]|$)/i,
    flag: AccountFeatureFlag.ExperimentAction,
    unlessBuilderPlan: true,
  },
  // An API-triggered broadcast's Overview tab (FPS-2666), one tab of a detail that migrates tab
  // by tab: each tab claims its own address behind its own flag, and the tab bar resolves the
  // rest per click. Claimed at this app's `/broadcasts/broadcast/:id/overview` and at both
  // `journeys` addresses Ember serves it at, the overview and the bare `/broadcast/:id` the list
  // rows link to, which Ember's index forwards to the overview (or a draft to setup, which the
  // tab's frame does here). Terminal, so the other tabs beneath `/overview/` stay unclaimed.
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/(?:journeys\/)?broadcasts\/broadcast\/\d+(?:\/overview)?\/?(?:[#?]|$)/i,
    flag: AccountFeatureFlag.ApiTriggeredBroadcastOverviewMigration,
  },
  // The CDP connector routes — `/integrations/cdp_in` and `/integrations/cdp_out` (FPS-2682).
  // Unflagged: `segment-cdp-integrations-migration` reached 100% including new signups on
  // 2026-09-09 and was retired in the FPS-2732 cutover, so these are Hydra's outright.
  //
  // Neither route is a page: each redirects into the Pipelines app. Both spellings are claimed for
  // the usual reason — the migrated URL is where the redirect lives, and the `journeys` twin stays
  // claimed so the integrations directory's two cards keep working without being re-pointed. Every
  // pattern terminates at the connector, so nothing deeper is claimed.
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/integrations\/cdp_in\/?(?:[#?]|$)/i,
  },
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/journeys\/integrations\/cdp_in\/?(?:[#?]|$)/i,
  },
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/integrations\/cdp_out\/?(?:[#?]|$)/i,
  },
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/journeys\/integrations\/cdp_out\/?(?:[#?]|$)/i,
  },
  // The Segment data-in connector — `/integrations/segment_in` (FPS-2721), unflagged with the
  // rest of the Segment/CDP family in the FPS-2732 cutover.
  //
  // Both Segment connectors are named from Segment's side, so the route segment and
  // the page title disagree with the direction of the data: `segment_in` is titled
  // "Segment destination" and feeds a workspace, while `segment_out` is titled
  // "Segment source" and sends to Segment. `segdotcom` is neither — it is the legacy
  // `segment_out` record.
  //
  // Both spellings, as with every connector. Nothing about the account decides the owner any
  // more — for `segment_out` either, since both of that connector's generations are served here.
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/integrations\/segment_in\/?(?:[#?]|$)/i,
  },
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/journeys\/integrations\/segment_in\/?(?:[#?]|$)/i,
  },
  // The Segment source connector — `/integrations/segment_out` (FPS-2696), unflagged with the
  // CDP redirects above in the FPS-2732 cutover.
  //
  // Both spellings, as with every connector: the migrated URL is where the page lives, and the
  // `journeys` twin stays claimed so the directory's card keeps working. The twin forwards
  // unconditionally, like the rest — it used to read the record and render the embedded app for a
  // workspace whose settings predate the current form, and that is the one thing a claimed URL
  // must not do: Ember asks `isHydraRoute` before transitioning, so it declined to move and left
  // the reader on whatever page they arrived from. Both generations have a form here now
  // (FPS-3689).
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/integrations\/segment_out\/?(?:[#?]|$)/i,
  },
  {
    owner: 'hydra',
    pattern: /^\/workspaces\/(last|(\d+))\/journeys\/integrations\/segment_out\/?(?:[#?]|$)/i,
  },
  // Design Studio dashboard (CON-7495), fully rolled out: the page is native
  // here and the embedded Design Studio bundle no longer carries a dashboard
  // route to fall back to. The pattern matches only the exact dashboard URL
  // (plus optional trailing slash, query, or hash): the native route is a
  // static path with no children, so `/dashboard/<sub>` and `/dashboard-*` URLs
  // route to the `/design-studio/$` splat and must stay with the embedded app —
  // ownership mirrors that.
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/design-studio\/dashboard\/?(?:[#?]|$)/i },
  // Design Studio global styles (CON-7588), fully rolled out: the page is
  // native here and the embedded Design Studio bundle no longer carries a
  // global-styles route to fall back to (CON-7922). The pattern matches only
  // the exact global-styles URL (plus optional trailing slash, query, or hash):
  // the native route is a static path with no children, so `/global-styles/<sub>`
  // and `/global-styles-*` URLs route to the `/design-studio/$` splat and must
  // stay with the embedded app — ownership mirrors that.
  { owner: 'hydra', pattern: /^\/workspaces\/(last|(\d+))\/design-studio\/global-styles\/?(?:[#?]|$)/i },
];

function matchesFeatureFlags({ flag, unlessFlag }: RouteOwnershipPattern, account: RouteOwnershipAccount): boolean {
  return (
    (flag == null || accountHasFeatureFlag(account, flag)) &&
    (unlessFlag == null || !accountHasFeatureFlag(account, unlessFlag))
  );
}

function matchesAccountConditions(entry: RouteOwnershipPattern, account: RouteOwnershipAccount): boolean {
  return (
    matchesFeatureFlags(entry, account) &&
    !(entry.unlessMasq === true && account?.is_masq === true) &&
    !(entry.unlessBuilderPlan === true && isBuilderPlan(account))
  );
}

/**
 * Paths a Hydra route has handed back to the embedded app after reading what the URL addresses,
 * one entry per claim: a route that claims a path its pattern entry gives to this app, because
 * the record behind it turns out to be one only the embedded app can show.
 */
const embeddedFallbackClaimsStore = atom<readonly string[]>([]);

/** A path as the claims compare it: the pathname alone, lowercased, without a trailing slash. */
function claimedPath(path: string): string {
  const pathname = (path.split(/[#?]/, 1)[0] ?? path).toLowerCase();
  return pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
}

/**
 * Hands a path to the embedded app for as long as the claim is held, whatever the pattern
 * entries say. The query string and the hash play no part. Returns the release.
 */
export function claimPathForEmbedded(path: string): () => void {
  const claim = claimedPath(path);
  embeddedFallbackClaimsStore.set([...embeddedFallbackClaimsStore.get(), claim]);

  let isReleased = false;
  return () => {
    if (isReleased) {
      return;
    }
    isReleased = true;
    const claims = embeddedFallbackClaimsStore.get();
    const index = claims.indexOf(claim);
    embeddedFallbackClaimsStore.set(claims.filter((_, claimIndex) => claimIndex !== index));
  };
}

/**
 * Subscribes a render-time ownership read to the claims, so it re-evaluates when a route hands
 * its path to the embedded app or takes it back. `useRouteOwner` already does.
 */
export function useEmbeddedFallbackClaims(): readonly string[] {
  return useStore(embeddedFallbackClaimsStore);
}

/**
 * Hands `path` to the embedded app while the calling route renders it there, and takes it back
 * when the path changes or the route unmounts. `null` claims nothing.
 *
 * Gotcha: the embedded app asks this map whether a URL it is told to show belongs to Hydra, and
 * ignores the URL when it does. The claim therefore lands in a layout effect, before the shell
 * first announces the route on a cold load. A booted embedded app was already told about this
 * URL while it still answered `hydra`, when the navigation started, and ignored it; the route is
 * announced again once the claim is in place, so the embedded app moves to it rather than
 * keeping the page it showed before.
 *
 * Back or Forward onto this URL from a page the embedded app rendered never puts it in the active
 * route: the active route still names that page. When the browser is on the claimed path, it is
 * announced instead, for the same reason.
 */
export function useEmbeddedFallbackClaim(path: string | null): void {
  useLayoutEffect(() => {
    if (path == null) {
      return undefined;
    }

    const release = claimPathForEmbedded(path);
    const activeRoute = activeRouteStore.get();
    if (activeRoute !== '') {
      const { pathname, search, hash } = window.location;
      const currentLocation = `${pathname}${search}${hash}`;
      if (claimedPath(activeRoute) === claimedPath(path)) {
        setActiveRoute(activeRoute);
      } else if (claimedPath(currentLocation) === claimedPath(path)) {
        setActiveRoute(currentLocation);
      }
    }
    return release;
  }, [path]);
}

/**
 * Route ownership is a function of the URL *and* the account's feature flags
 * (flag-gated migration entries), so the account is a required input. A null
 * account means "not loaded yet" — flags read their defaults, so flag-gated
 * Hydra routes classify as embedded until the real account is supplied. A path
 * a route has claimed for the embedded app (`claimPathForEmbedded`) answers
 * `embedded` whatever the entries say.
 *
 * Call-time consumers (event handlers, the Ember `window.__hydra__` bridge)
 * pass `accountStore.get()`. Render-time consumers must NOT — a component
 * rendered before auth completes would cache the flag-default answer
 *; use `useRouteOwner`/`useIsHydraRoute` instead.
 */
export function getRouteOwner(path: string, account: RouteOwnershipAccount): RouteOwner {
  if (embeddedFallbackClaimsStore.get().includes(claimedPath(path))) {
    return 'embedded';
  }

  return (
    ROUTE_OWNERSHIP_PATTERNS.find((entry) => entry.pattern.test(path) && matchesAccountConditions(entry, account))
      ?.owner ?? 'embedded'
  );
}

export function isHydraRoute(path: string, account: RouteOwnershipAccount): boolean {
  return getRouteOwner(path, account) === 'hydra';
}

/**
 * Reactive ownership for render-time consumers: subscribes to the account so
 * flag-gated entries re-evaluate when it loads.
 */
export function useRouteOwner(path: string): RouteOwner {
  const account = useStore(accountStore);
  useEmbeddedFallbackClaims();
  return getRouteOwner(path, account);
}

export function useIsHydraRoute(path: string): boolean {
  return useRouteOwner(path) === 'hydra';
}
